import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useJsApiLoader } from '@react-google-maps/api';
import { useAuth } from '../../../components/contexts/AuthContext';
import { mapsConfig } from '../models/mapsApi';

// The single Google Maps loader. The loader throws if it is called again with
// different options, so no other component may call useJsApiLoader. No Places
// library: search and geocoding go through the server.
const LIBRARIES = [];
const UNAVAILABLE = new Error('Map unavailable. Search and pasted coordinates still work.');
const MapsContext = createContext({ isLoaded: false, loadError: null, unavailable: false });

function LoaderProbe({ browserKey, onStatus }) {
  const { isLoaded, loadError } = useJsApiLoader({ id: 'srm-google-maps', googleMapsApiKey: browserKey, libraries: LIBRARIES });
  useEffect(() => { onStatus({ isLoaded, loadError: loadError || null }); }, [isLoaded, loadError, onStatus]);
  return null;
}

export function MapsProvider({ children }) {
  const { authState } = useAuth();
  const signedIn = Boolean(authState?.isAuthenticated);
  const [browserKey, setBrowserKey] = useState(null);
  const [requested, setRequested] = useState(false);
  const requestMap = useCallback(() => setRequested(true), []);
  const [unavailable, setUnavailable] = useState(false);
  const [status, setStatus] = useState({ isLoaded: false, loadError: null });
  useEffect(() => {
    if (!signedIn || !requested || browserKey) return undefined;
    let live = true;
    setUnavailable(false);
    mapsConfig()
      .then(data => { if (!live) return; if (typeof data?.browser_key === 'string' && data.browser_key) setBrowserKey(data.browser_key); else setUnavailable(true); })
      .catch(() => { if (live) setUnavailable(true); });
    return () => { live = false; };
  }, [signedIn, requested, browserKey]);
  const value = useMemo(() => ({ requestMap, isLoaded: status.isLoaded, loadError: status.loadError || (unavailable ? UNAVAILABLE : null), unavailable: unavailable || Boolean(status.loadError) }), [requestMap, status, unavailable]);
  // The probe is a sibling, so the app tree never remounts when the key arrives.
  return <MapsContext.Provider value={value}>{browserKey && <LoaderProbe browserKey={browserKey} onStatus={setStatus} />}{children}</MapsContext.Provider>;
}

// Closed pickers/parent forms may observe status without initiating a download.
export const useMaps = (active = true) => {
  const maps = useContext(MapsContext);
  const requestMap = maps.requestMap;
  useEffect(() => { if (active) requestMap?.(); }, [active, requestMap]);
  return maps;
};
